// ═══════════════════════════════════════════════════════════════
// HOOK: usePrestamos - CORREGIDO con companyId
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { prestamosAPI } from '../api/prestamos';
import { useCompany } from '../contexts/CompanyContext';

export function usePrestamos() {
  const { currentCompany } = useCompany();
  const [prestamos, setPrestamos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentCompany) {
      loadPrestamos();
    }
  }, [currentCompany]);

  /**
   * Transformar datos de la API al formato del componente
   */
  const transformPrestamo = (prestamoFromAPI) => {
    return {
      id: prestamoFromAPI.id,
      empleadoId: prestamoFromAPI.empleadoId || prestamoFromAPI.employee_id,
      empleadoNombre: prestamoFromAPI.empleadoNombre || prestamoFromAPI.empleado_nombre,
      empleadoCodigo: prestamoFromAPI.empleadoCodigo || prestamoFromAPI.empleado_codigo,
      fechaSolicitud: prestamoFromAPI.fechaSolicitud || prestamoFromAPI.fecha_solicitud,
      fechaAprobacion: prestamoFromAPI.fechaAprobacion || prestamoFromAPI.fecha_aprobacion,
      valorSolicitado: parseFloat(prestamoFromAPI.valorSolicitado || prestamoFromAPI.valor_solicitado || 0),
      tasaInteres: parseFloat(prestamoFromAPI.tasaInteres || prestamoFromAPI.tasa_interes || 0),
      montoInteres: parseFloat(prestamoFromAPI.montoInteres || prestamoFromAPI.monto_interes || 0),
      valorTotal: parseFloat(prestamoFromAPI.valorTotal || prestamoFromAPI.valor_total || 0),
      cuotaMensual: parseFloat(prestamoFromAPI.cuotaMensual || prestamoFromAPI.cuota_mensual || 0),
      cuotas: parseInt(prestamoFromAPI.cuotas || 0),
      tiempoMeses: parseInt(prestamoFromAPI.tiempoMeses || prestamoFromAPI.tiempo_meses || 0),
      cuotasPagadas: parseInt(prestamoFromAPI.cuotasPagadas || prestamoFromAPI.cuotas_pagadas || 0),
      valorPagado: parseFloat(prestamoFromAPI.valorPagado || prestamoFromAPI.valor_pagado || 0),
      balance: parseFloat(prestamoFromAPI.balance || 0),
      quincenaDescuento: prestamoFromAPI.quincenaDescuento || prestamoFromAPI.quincena_descuento || 'Q1',
      estado: prestamoFromAPI.estado || 'activo',
      comentarios: prestamoFromAPI.comentarios || '',
      historialPagos: prestamoFromAPI.historialPagos || prestamoFromAPI.historial_pagos || []
    };
  };

  /**
   * Cargar préstamos desde la API
   */
  const loadPrestamos = async () => {
    try {
      setLoading(true);
      const data = await prestamosAPI.getAll();
      
      // Transformar cada préstamo
      const prestamosTransformados = Array.isArray(data) 
        ? data.map(transformPrestamo)
        : [];
      
      console.log('✅ Préstamos cargados:', prestamosTransformados);
      setPrestamos(prestamosTransformados);
      setError(null);
    } catch (err) {
      console.error('❌ Error loading prestamos:', err);
      setError(err.message);
      setPrestamos([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Crear préstamo
   */
  const createPrestamo = async (prestamoData) => {
    try {
      // 🔥 FIX: Incluir companyId
      if (!currentCompany?.id) {
        throw new Error('No hay compañía seleccionada');
      }

      const response = await prestamosAPI.create({
        companyId: currentCompany.id, // ← AGREGADO
        empleadoId: prestamoData.empleadoId,
        empleadoNombre: prestamoData.empleadoNombre,
        empleadoCodigo: prestamoData.empleadoCodigo,
        fechaSolicitud: prestamoData.fechaSolicitud,
        valorSolicitado: prestamoData.valorSolicitado,
        tasaInteres: prestamoData.tasaInteres,
        montoInteres: prestamoData.montoInteres,
        valorTotal: prestamoData.valorTotal,
        cuotaMensual: prestamoData.cuotaMensual,
        cuotas: prestamoData.cuotas,
        tiempoMeses: prestamoData.tiempoMeses,
        quincenaDescuento: prestamoData.quincenaDescuento,
        comentarios: prestamoData.comentarios
      });

      await loadPrestamos(); // Recargar lista
      return response;
    } catch (err) {
      console.error('❌ Error creating prestamo:', err);
      throw err;
    }
  };

  /**
   * Actualizar préstamo
   */
  const updatePrestamo = async (id, updates) => {
    try {
      // Mantener los nombres camelCase ya que el backend los acepta
      const response = await prestamosAPI.update(id, updates);
      await loadPrestamos(); // Recargar lista
      return response;
    } catch (err) {
      console.error('❌ Error updating prestamo:', err);
      throw err;
    }
  };

  /**
   * Eliminar préstamo
   */
  const deletePrestamo = async (id) => {
    try {
      await prestamosAPI.delete(id);
      await loadPrestamos(); // Recargar lista
    } catch (err) {
      console.error('❌ Error deleting prestamo:', err);
      throw err;
    }
  };

  /**
   * Registrar pago manualmente
   */
  const registrarPago = async (prestamoId) => {
    const prestamo = prestamos.find(p => p.id === prestamoId);
    if (!prestamo) return;

    if (prestamo.cuotasPagadas >= prestamo.cuotas) {
      throw new Error('Este préstamo ya está completamente pagado');
    }

    const nuevasCuotasPagadas = prestamo.cuotasPagadas + 1;
    const nuevoValorPagado = prestamo.valorPagado + prestamo.cuotaMensual;
    const nuevoBalance = prestamo.valorTotal - nuevoValorPagado;
    const nuevoEstado = nuevasCuotasPagadas >= prestamo.cuotas ? 'completado' : 'activo';

    await updatePrestamo(prestamoId, {
      cuotasPagadas: nuevasCuotasPagadas,
      valorPagado: nuevoValorPagado,
      balance: nuevoBalance >= 0 ? nuevoBalance : 0,
      estado: nuevoEstado
    });
  };

  /**
   * Reversar último pago
   */
  const reversarPago = async (prestamoId) => {
    const prestamo = prestamos.find(p => p.id === prestamoId);
    if (!prestamo) return;

    if (prestamo.cuotasPagadas === 0) {
      throw new Error('No hay pagos registrados para reversar');
    }

    const nuevasCuotasPagadas = prestamo.cuotasPagadas - 1;
    const nuevoValorPagado = prestamo.valorPagado - prestamo.cuotaMensual;
    const nuevoBalance = prestamo.valorTotal - nuevoValorPagado;

    await updatePrestamo(prestamoId, {
      cuotasPagadas: nuevasCuotasPagadas,
      valorPagado: nuevoValorPagado >= 0 ? nuevoValorPagado : 0,
      balance: nuevoBalance,
      estado: 'activo'
    });
  };

  return {
    prestamos,
    loading,
    error,
    createPrestamo,
    updatePrestamo,
    deletePrestamo,
    registrarPago,
    reversarPago,
    reload: loadPrestamos
  };
}

export default usePrestamos;
